'use client';

import { useCallback, useState } from 'react';
import Image from '@alltrails/core/components/Image';
import PhotoSlash from '@alltrails/denali/icons/PhotoSlash';
import styles from './PhotoGrid.module.scss';

const PhotoFallback = () => (
  <div className={styles.photoFallback}>
    <PhotoSlash color="--color-text-secondary" />
  </div>
);

type GridPhotoProps = {
  src?: string;
  failed: boolean;
  className?: string;
  onClick?: () => void;
  onError?: () => void;
};

const GridPhoto = ({ src, failed, className, onClick, onError }: GridPhotoProps) => {
  if (!src || failed) {
    return (
      <div className={className}>
        <PhotoFallback />
      </div>
    );
  }

  return (
    <button className={className} onClick={onClick}>
      <Image alt="" className={styles.image} fill src={src} handleLoadingState onError={onError} />
    </button>
  );
};

export type PhotoGridPhoto = {
  src?: string;
  onClick?: () => void;
};

type PhotoGridProps = {
  // Pass 1 entry to render the single-image layout, or 2-3 entries to render the grid layout.
  // The grid auto-collapses to single-image when 2+ tiles fail to load.
  photos: PhotoGridPhoto[];
};

const PhotoGrid = ({ photos }: PhotoGridProps) => {
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());

  const onImageError = useCallback((index: number) => {
    setFailedImages(prev => new Set(prev).add(index));
  }, []);

  if (photos.length === 0) {
    return null;
  }

  const isGrid = photos.length > 1;
  const tooManyFailures = isGrid && failedImages.size >= 2;
  const showSingleImage = !isGrid || tooManyFailures;

  if (showSingleImage) {
    const firstUsable = tooManyFailures ? photos.findIndex((photo, i) => !failedImages.has(i) && photo.src) : 0;
    const index = firstUsable === -1 ? 0 : firstUsable;
    const photo = photos[index];
    return (
      <GridPhoto
        src={photo.src}
        failed={failedImages.has(index)}
        className={styles.singleImage}
        onClick={photo.onClick}
        onError={() => onImageError(index)}
      />
    );
  }

  return (
    <div className={styles.grid}>
      <GridPhoto
        src={photos[0]?.src}
        failed={failedImages.has(0)}
        className={`${styles.imageContainer} ${styles.primaryImage}`}
        onClick={photos[0]?.onClick}
        onError={() => onImageError(0)}
      />
      <GridPhoto
        src={photos[1]?.src}
        failed={failedImages.has(1)}
        className={`${styles.imageContainer} ${styles.topRightImage}`}
        onClick={photos[1]?.onClick}
        onError={() => onImageError(1)}
      />
      <GridPhoto
        src={photos[2]?.src}
        failed={failedImages.has(2)}
        className={`${styles.imageContainer} ${styles.bottomRightImage}`}
        onClick={photos[2]?.onClick}
        onError={() => onImageError(2)}
      />
    </div>
  );
};

export default PhotoGrid;
